import { useMemo, useState } from "react";
import type { ProductDto } from "../api";
import type { ChosenModifier } from "../state/cart";
import { exclusionLabel, money, Photo } from "../ui";

type Group = ProductDto["modifiers"][number];

/**
 * Выбор состава перед добавлением в корзину (п. 4.3–4.4 ТЗ).
 *
 * Пока обязательные группы не заполнены, добавить товар нельзя, и кнопка
 * прямо говорит, чего не хватает, — иначе гость упирается в неактивную кнопку
 * и не понимает почему.
 */
export function ProductSheet({
  product,
  onAdd,
  onClose
}: {
  product: ProductDto;
  onAdd: (modifiers: ChosenModifier[], qty: number) => void;
  onClose: () => void;
}) {
  const groups = product.modifiers.filter(group => group.available);
  const [picked, setPicked] = useState<Record<string, { optionId: string; qty: number }[]>>(() => {
    // Обязательный выбор из одного варианта подставляется сразу: гостю
    // незачем подтверждать единственно возможное.
    const initial: Record<string, { optionId: string; qty: number }[]> = {};
    for (const group of groups) {
      const options = group.options.filter(option => option.available);
      if (group.required && options.length === 1) initial[group.id] = [{ optionId: options[0]!.id, qty: 1 }];
    }
    return initial;
  });
  const [qty, setQty] = useState(1);

  const missing = groups.filter(group => group.required && (picked[group.id]?.length ?? 0) < group.min);

  const chosen: ChosenModifier[] = useMemo(() => {
    return groups.flatMap(group =>
      (picked[group.id] ?? []).map(entry => {
        const option = group.options.find(candidate => candidate.id === entry.optionId)!;
        return {
          groupId: group.id,
          optionId: option.id,
          title: option.title,
          price: option.price,
          qty: entry.qty,
          exclusion: option.exclusion
        };
      })
    );
  }, [groups, picked]);

  const unitPrice = product.price + chosen.reduce((sum, modifier) => sum + modifier.price * modifier.qty, 0);

  function toggle(group: Group, optionId: string) {
    setPicked(current => {
      const selected = current[group.id] ?? [];
      const already = selected.find(entry => entry.optionId === optionId);

      if (group.max === 1) {
        return { ...current, [group.id]: already && !group.required ? [] : [{ optionId, qty: 1 }] };
      }

      if (already) {
        return { ...current, [group.id]: selected.filter(entry => entry.optionId !== optionId) };
      }

      if (selected.length >= group.max) return current;
      return { ...current, [group.id]: [...selected, { optionId, qty: 1 }] };
    });
  }

  function changeQty(groupId: string, optionId: string, delta: number, maxQty: number) {
    setPicked(current => ({
      ...current,
      [groupId]: (current[groupId] ?? []).map(entry =>
        entry.optionId === optionId
          ? { ...entry, qty: Math.min(maxQty, Math.max(1, entry.qty + delta)) }
          : entry
      )
    }));
  }

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label={product.title}
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="sheet">
        <div className="sheet-photo">
          <Photo src={product.imageUrl} title={product.title} />
        </div>

        <div className="sheet-head">
          <h2>{product.title}</h2>
          <small>
            {[product.weight, product.description].filter(Boolean).join(" · ")}
          </small>
        </div>

        {product.composition ? <p className="tiny muted">Состав: {product.composition}</p> : null}

        {groups.map(group => {
          const selected = picked[group.id] ?? [];

          return (
            <div className="opt-group" key={group.id}>
              <div className="opt-title">
                {group.title}
                <em>
                  {group.required ? "обязательно" : "по желанию"}
                  {group.max > 1 ? `, до ${group.max}` : ""}
                </em>
              </div>

              {group.options.map(option => {
                const entry = selected.find(item => item.optionId === option.id);
                const isPicked = Boolean(entry);

                return (
                  <div
                    className={`opt ${isPicked ? "picked" : ""} ${option.available ? "" : "off"}`}
                    key={option.id}
                    role="button"
                    tabIndex={option.available ? 0 : -1}
                    onClick={() => option.available && toggle(group, option.id)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        if (option.available) toggle(group, option.id);
                      }
                    }}
                  >
                    <b>{option.exclusion ? exclusionLabel(option.title) : option.title}</b>

                    {isPicked && option.maxQty > 1 && !option.exclusion ? (
                      <span className="stepper" onClick={event => event.stopPropagation()}>
                        <button
                          type="button"
                          aria-label="Меньше"
                          disabled={entry!.qty <= 1}
                          onClick={() => changeQty(group.id, option.id, -1, option.maxQty)}
                        >
                          −
                        </button>
                        <b>{entry!.qty}</b>
                        <button
                          type="button"
                          aria-label="Больше"
                          disabled={entry!.qty >= option.maxQty}
                          onClick={() => changeQty(group.id, option.id, 1, option.maxQty)}
                        >
                          +
                        </button>
                      </span>
                    ) : (
                      <span className="price">
                        {!option.available ? "закончилось" : option.price ? `+${money(option.price)}` : "включено"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span className="stepper" style={{ background: "var(--surface)" }}>
            <button type="button" aria-label="Меньше" disabled={qty <= 1} onClick={() => setQty(qty - 1)}>
              −
            </button>
            <b>{qty}</b>
            <button type="button" aria-label="Больше" disabled={qty >= 20} onClick={() => setQty(qty + 1)}>
              +
            </button>
          </span>

          <button
            className="wide-btn"
            type="button"
            style={{ flex: 1 }}
            disabled={missing.length > 0}
            onClick={() => onAdd(chosen, qty)}
          >
            {missing.length > 0
              ? `Выберите: ${missing.map(group => group.title.toLowerCase()).join(", ")}`
              : `Добавить за ${money(unitPrice * qty)}`}
          </button>
        </div>

        <button className="wide-btn ghost" type="button" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </div>
  );
}
